import React, { useState, useEffect } from 'react';
import { ChevronDown, Filter, RefreshCw } from '../ui/icons';
import { AuditLogEntry, AuditLogQuery } from '../../models/audit-log';
import { auditService } from '../../services/auditService';
import {
  Card,
  CardContent,
  Badge,
  Select,
  Input,
  Button,
  Alert,
  Spinner,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '../ui';

interface AuditLogsListProps {
  sourceId: string;
}

export const AuditLogsList: React.FC<AuditLogsListProps> = ({ sourceId }) => {
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nextToken, setNextToken] = useState<string | undefined>();
  const [hasMore, setHasMore] = useState(false);

  // Filters
  const [logType, setLogType] = useState<string>('');
  const [level, setLevel] = useState<string>('');
  const [startTime, setStartTime] = useState<string>('');
  const [endTime, setEndTime] = useState<string>('');
  const [limit] = useState(50);

  const loadLogs = async (reset: boolean = false) => {
    try {
      setLoading(true);
      setError(null);

      const query: Partial<AuditLogQuery> = {
        limit,
        ...(logType && { logType: logType as any }),
        ...(level && { level: level as any }),
        ...(startTime && { startTime }),
        ...(endTime && { endTime }),
        ...(reset ? {} : { nextToken }),
      };

      const response = await auditService.getAuditLogs(sourceId, query);
      
      if (reset) {
        setLogs(response.logs);
      } else {
        setLogs(prev => [...prev, ...response.logs]);
      }
      
      setNextToken(response.nextToken);
      setHasMore(!!response.nextToken);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs(true);
  }, [sourceId, logType, level, startTime, endTime]);

  const handleRefresh = () => {
    loadLogs(true);
  };

  const handleLoadMore = () => {
    if (!loading && hasMore) {
      loadLogs(false);
    }
  };

  const handleClearFilters = () => {
    setLogType('');
    setLevel('');
    setStartTime('');
    setEndTime('');
  };

  const getLogLevelColor = (logLevel: AuditLogEntry['level']): 'info' | 'warning' | 'error' | 'success' | 'default' => {
    const colors = {
      'INFO': 'info' as const,
      'WARN': 'warning' as const,
      'ERROR': 'error' as const,
      'SUCCESS': 'success' as const,
    };
    return colors[logLevel] || 'default';
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const renderLogDetails = (log: AuditLogEntry) => {
    if (!log.details) return null;

    return (
      <div className="mt-2 p-4 bg-gray-50 rounded-lg">
        <p className="text-xs text-gray-600 mb-2">Details:</p>
        <pre className="text-xs my-2 whitespace-pre-wrap">
          {JSON.stringify(log.details, null, 2)}
        </pre>
      </div>
    );
  };

  if (loading && logs.length === 0) {
    return (
      <div className="flex justify-center p-12">
        <Spinner size="lg" className="text-primary-600" />
      </div>
    );
  }

  return (
    <div>
      {/* Filters */}
      <Card className="mb-4">
        <CardContent>
          <h6 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <Filter className="h-4 w-4 mr-2" />
            Filters
          </h6>

          <div className="grid grid-cols-12 gap-4 items-end">
            <div className="col-span-12 sm:col-span-6 md:col-span-3">
              <Select
                label="Log Type"
                value={logType}
                onChange={(e) => setLogType(e.target.value)}
                options={[
                  { value: '', label: 'All Types' },
                  { value: 'SOURCE_PROCESSING', label: 'Source Processing' },
                  { value: 'URL_PROCESSING', label: 'URL Processing' },
                  { value: 'PROPERTY_CHANGE', label: 'Property Changes' },
                  { value: 'FILTERING', label: 'Filtering' },
                  { value: 'ERROR', label: 'Errors' },
                  { value: 'CRON_JOB', label: 'Cron Jobs' },
                ]}
              />
            </div>

            <div className="col-span-12 sm:col-span-6 md:col-span-3">
              <Select
                label="Level"
                value={level}
                onChange={(e) => setLevel(e.target.value)}
                options={[
                  { value: '', label: 'All Levels' },
                  { value: 'INFO', label: 'Info' },
                  { value: 'WARN', label: 'Warning' },
                  { value: 'ERROR', label: 'Error' },
                  { value: 'SUCCESS', label: 'Success' },
                ]}
              />
            </div>

            <div className="col-span-12 sm:col-span-6 md:col-span-2">
              <Input
                label="Start Time"
                type="datetime-local"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
              />
            </div>

            <div className="col-span-12 sm:col-span-6 md:col-span-2">
              <Input
                label="End Time"
                type="datetime-local"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
              />
            </div>

            <div className="col-span-12 md:col-span-2">
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClearFilters}
                >
                  Clear
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRefresh}
                >
                  <RefreshCw className="h-4 w-4 mr-1" />
                  Refresh
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error Display */}
      {error && (
        <Alert variant="error" className="mb-4">
          {error}
        </Alert>
      )}

      {/* Logs List */}
      <Card>
        <CardContent>
          <h6 className="text-lg font-medium text-gray-900 mb-4">
            Audit Logs ({logs.length} entries)
          </h6>

          {logs.length === 0 ? (
            <p className="text-gray-600 text-center py-8">
              No audit logs found for the selected filters.
            </p>
          ) : (
            <div className="space-y-4">
              {logs.map((log, index) => (
                <div
                  key={`${log.pk}-${log.sk}-${index}`}
                  className="border-b border-gray-200 pb-4 last:border-b-0"
                >
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <Badge
                      variant={getLogLevelColor(log.level)}
                      size="sm"
                    >
                      {log.level}
                    </Badge>
                    <Badge
                      variant="secondary"
                      size="sm"
                    >
                      {auditService.getLogTypeDisplayName(log.logType)}
                    </Badge>
                    <span className="text-xs text-gray-600">
                      {formatTimestamp(log.timestamp)}
                    </span>
                  </div>

                  <p className="text-sm text-gray-900 mb-2">
                    {log.message}
                  </p>

                  {log.details && (
                    <Accordion className="mt-2">
                      <AccordionSummary expandIcon={<ChevronDown className="h-4 w-4" />}>
                        <span className="text-xs text-gray-600">
                          View Details
                        </span>
                      </AccordionSummary>
                      <AccordionDetails>
                        {renderLogDetails(log)}
                      </AccordionDetails>
                    </Accordion>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Load More Button */}
          {hasMore && (
            <div className="flex justify-center mt-4">
              <Button
                variant="outline"
                onClick={handleLoadMore}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner size="sm" className="mr-2" />
                    Loading...
                  </>
                ) : (
                  'Load More'
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
